import React, { useState } from "react";
import {
  useForm,
  type Control,
  type FieldValues,
  type Path,
  type DefaultValues,
  type UseFormReturn,
  type Resolver,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import type { ZodTypeAny } from "zod";
import { Input } from "@components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/ui/form";
import { BusyButton } from "./BusyButton";
import { FileImageField } from "./FileImageField";

// ==== Tipos de campo soportados por defecto ====
export type BuiltinFieldType =
  | "text"
  | "email"
  | "password"
  | "color" // tu ColorPicker
  | "selectFont" // tu SelectTipografia
  | "fileImage" // archivo (logo)
  | "selectIntermediario"; // select con búsqueda y paginación

export type FieldRendererProps<TForm extends FieldValues> = {
  name: Path<TForm>;
  label?: string;
  control: Control<TForm>;
  disabled?: boolean;
  required?: boolean;
  onLocalChange?: (file: File | null) => void;
  colSpan?: "1" | "2";
  placeholder?: string;
  description?: string;
  file?: File | null;
  previewBackground?: "light" | "dark";
  clearable?: boolean;
};

// Renderizador custom por campo
export type CustomRenderer<TForm extends FieldValues> = (
  p: FieldRendererProps<TForm>
) => React.ReactNode;

export type FieldConfig<TForm extends FieldValues> = {
  name: Path<TForm>;
  label?: string;
  colSpan?: "1" | "2";
  type?: BuiltinFieldType;
  required?: boolean;
  disabled?: boolean;
  // Para casos especiales: sobreescribir renderer
  render?: CustomRenderer<TForm>;
  // Props extras específicos de algunos renderers
  placeholder?: string;
  description?: string;
  // fileImage: fondo de la vista previa y opción de quitar la imagen
  previewBackground?: "light" | "dark";
  clearable?: boolean;
};

export type FormBuilderProps<TForm extends FieldValues> = {
  schema: ZodTypeAny;
  defaultValues?: DefaultValues<TForm>;
  fields: FieldConfig<TForm>[];
  onSubmit: (
    data: TForm,
    helpers: { fileBag: Record<string, File | null> },
  ) => Promise<void> | void;
  submitLabel: string;
  isSubmitting?: boolean;
  // Área para UI especial (p.ej. preview). Recibe form, y helpers.
  renderAfter?: (ctx: {
    form: UseFormReturn<TForm>;
    fileBag: Record<string, File | null>;
    setFile: (fieldName: Path<TForm>, file: File | null) => void;
  }) => React.ReactNode;
  // Registry para “color”, “selectFont” u otros custom
  registry?: Partial<Record<BuiltinFieldType, CustomRenderer<TForm>>>;
};

export function FormBuilder<TForm extends FieldValues>({
  schema,
  defaultValues,
  fields,
  onSubmit,
  submitLabel,
  isSubmitting,
  renderAfter,
  registry = {},
}: FormBuilderProps<TForm>) {
  const form = useForm<TForm>({
    resolver: zodResolver(schema) as Resolver<TForm>,
    defaultValues,
    mode: "onChange",
  });

  const handleFormSubmit = async (data: TForm) => {
    try {
      await onSubmit(data, { fileBag });
    } catch (error) {
      // Los errores de la petición los notifica cada mutación; aquí llegan
      // los que ocurren antes de enviarla (p. ej. al subir un archivo).
      console.error("Submit error:", error);
      toast.error("No se pudieron guardar los cambios", {
        description:
          "Falló la subida de un archivo o la preparación de los datos. Revisa tu conexión e inténtalo de nuevo.",
      });
    }
  };

  // fileBag: almacena archivos locales por nombre de campo (p.ej. "cliente.logoMarcaFile")
  const [fileBag, setFileBag] = useState<Record<string, File | null>>({});

  const setFile = (fieldName: Path<TForm>, file: File | null) => {
    setFileBag((prev) => ({ ...prev, [fieldName as string]: file }));
  };

  // Renderers por defecto (puedes ajustar input types, etc.)
  const defaultRegistry: Partial<
    Record<BuiltinFieldType, CustomRenderer<TForm>>
  > = {
    text: ({ name, label, control, disabled, required, placeholder }) => (
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            {label && <FormLabel required={required}>{label}</FormLabel>}
            <FormControl>
              <Input placeholder={placeholder} disabled={disabled} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    ),
    email: ({ name, label, control, disabled, required, placeholder }) => (
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            {label && <FormLabel required={required}>{label}</FormLabel>}
            <FormControl>
              <Input
                type="email"
                placeholder={placeholder}
                disabled={disabled}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    ),
    password: ({ name, label, control, disabled, required, placeholder }) => (
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            {label && <FormLabel required={required}>{label}</FormLabel>}
            <FormControl>
              <Input
                type="password"
                placeholder={placeholder}
                disabled={disabled}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    ),
    // “color” y “selectFont” se delegan al registry externo del proyecto
    color: registry.color,
    selectFont: registry.selectFont,
    selectIntermediario: registry.selectIntermediario,

    // fileImage: el File se guarda en fileBag, no en RHF
    fileImage: (p) => <FileImageField<TForm> {...p} />,
  };

  const renderField = (cfg: FieldConfig<TForm>) => {
    // Renderer personalizado tiene prioridad
    if (cfg.render) {
      return (
        <div key={cfg.name} className={cfg.colSpan === "2" ? "md:col-span-2" : ""}>
          {cfg.render({
            name: cfg.name,
            label: cfg.label,
            control: form.control,
            disabled: cfg.disabled,
            required: cfg.required,
            placeholder: cfg.placeholder,
          })}
        </div>
      );
    }

    // Renderer builtin
    const builtin = cfg.type ?? "text";
    const renderer = defaultRegistry[builtin];
    if (!renderer) {
      // Fallback a input text
      return (
        <div key={cfg.name} className={cfg.colSpan === "2" ? "md:col-span-2" : ""}>
          {defaultRegistry.text?.({
            name: cfg.name,
            label: cfg.label,
            control: form.control,
            disabled: cfg.disabled,
            required: cfg.required,
            placeholder: cfg.placeholder,
          })}
        </div>
      );
    }

    if (builtin === "fileImage") {
      return (
        <div key={cfg.name} className={cfg.colSpan === "2" ? "md:col-span-2" : ""}>
          {renderer({
            name: cfg.name,
            label: cfg.label,
            control: form.control,
            required: cfg.required,
            onLocalChange: (f) => setFile(cfg.name, f),
            placeholder: cfg.placeholder,
            description: cfg.description,
            file: fileBag[cfg.name as string] ?? null,
            previewBackground: cfg.previewBackground,
            clearable: cfg.clearable,
          })}
        </div>
      );
    }

    // render normal
    return (
      <div
        key={cfg.name}
        className={cfg.colSpan === "2" ? "md:col-span-2" : ""}
      >
        {renderer({
          name: cfg.name,
          label: cfg.label,
          control: form.control,
          disabled: cfg.disabled,
          required: cfg.required,
          placeholder: cfg.placeholder,
        })}
      </div>
    );
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="space-y-6 w-full"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((field) => renderField(field))}
        </div>

        <BusyButton type="submit" disabled={!!isSubmitting} className="w-full">
          {submitLabel}
        </BusyButton>

        {renderAfter?.({ form, fileBag, setFile })}
      </form>
    </Form>
  );
}
